import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axios';

/**
 * جلب الصلاحيات من السيرفر
 */
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        return rejectWithValue('لم يتم العثور على توكن المستخدم');
      }

      // اختيار المسار الصحيح حسب نوع المستخدم
      const userType = sessionStorage.getItem('userType');
      const apiUrl = userType === 'admin' ? '/api/admin/permissions' : '/api/user/permissions';
      
      const response = await axiosInstance.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ أثناء جلب الصلاحيات');
    }
  }
);

/**
 * تحديث صلاحيات دور محدد
 */
export const updateRolePermissions = createAsyncThunk(
  'permissions/updateRolePermissions',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        return rejectWithValue('لم يتم العثور على توكن المستخدم');
      }
      
      const response = await axiosInstance.post(`/api/admin/roles/${roleId}/permissions`, {
        permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return { roleId, permissions, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ أثناء تحديث الصلاحيات');
    }
  }
);

// حالة بدء التشغيل للصلاحيات
const initialPermissionsState = {
  permissions: [],
  roleId: null,
  loading: false,
  initialized: false,
  error: null,
  lastFetch: null,
  // إضافة حالة المصادقة للمساعدة في عملية المزامنة
  auth: {
    isAuthenticated: false,
    token: null,
    userType: null,
    userRoleId: null,
    user: null
  }
};

// إنشاء Redux Slice للصلاحيات
const permissionsSlice = createSlice({
  name: 'permissions',
  initialState: initialPermissionsState,
  reducers: {
    // تحديث الصلاحيات يدوياً
    updatePermissions: (state, action) => {
      const { permissions, roleId } = action.payload;
      state.permissions = permissions;
      if (roleId) state.roleId = roleId;
      state.lastFetch = Date.now();
      
      // تحديث الصلاحيات في sessionStorage وCookies للتوافق مع بقية التطبيق
      try {
        sessionStorage.setItem('permissions', JSON.stringify(permissions));
        // لم نعد نستخدم الكوكيز، سنعتمد على API لكل تبويب جديد
        sessionStorage.setItem('permissions_timestamp', Date.now().toString());
      } catch (error) {
        // خطأ في تحديث الصلاحيات في التخزين
      }
    },

    // ضبط الصلاحيات (تستخدم للتحديث المباشر من API)
    setPermissions: (state, action) => {
      state.permissions = action.payload;
      state.lastFetch = Date.now();
      
      // تحديث الصلاحيات في sessionStorage وCookies بعد الحصول عليها
      try {
        sessionStorage.setItem('permissions', JSON.stringify(action.payload));
        // لم نعد نستخدم الكوكيز، سنعتمد على API لكل تبويب جديد
        sessionStorage.setItem('permissions_timestamp', Date.now().toString());
      } catch (error) {
        console.error('خطأ في حفظ الصلاحيات الجديدة:', error);
      }
    },
    
    // مسح الصلاحيات (عند تسجيل الخروج مثلاً)
    clearPermissions: (state) => {
      state.permissions = [];
      state.roleId = null;
      state.initialized = false;
      state.lastFetch = null;
      // لم نعد نستخدم الكوكيز
    },
    
    // تعيين علامة التهيئة
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
    
    // تحديث حالة المصادقة مباشرة (تستخدم للمزامنة بين التبويبات)
    setAuthState: (state, action) => {
      state.auth = action.payload;
      state.initialized = true;
      // تم تحديث حالة المصادقة في Redux مباشرة
      
      // تعيين الـ roleId من بيانات المصادقة للتوافق
      if (action.payload.userRoleId) {
        state.roleId = action.payload.userRoleId;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // حالات جلب الصلاحيات
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        
        // تحليل الاستجابة من API
        
        // التحقق ما إذا كان المستخدم مديرًا
        const userRole = sessionStorage.getItem('userRole');
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const isAdmin = userRole === 'admin' || user.role_id === 1;
        
        // التعامل مع كلا التنسيقين: الكائن والمصفوفة المباشرة
        let perms = [];
        
        // التحقق مما إذا كانت البيانات مصفوفة مباشرة
        if (Array.isArray(action.payload)) {
          perms = action.payload;
          // البيانات جاءت كمصفوفة مباشرة
        }
        // التحقق مما إذا كانت البيانات متضمنة في خاصية permissions
        else if (action.payload && action.payload.permissions) {
          perms = action.payload.permissions;
          // البيانات جاءت ككائن بخاصية permissions
        }
        // إذا كان الرد من API فارغًا والمستخدم مديرًا، نضيف جميع الصلاحيات المعروفة
        
        if (isAdmin && (!perms || perms.length === 0)) {
          // قائمة الصلاحيات المعروفة للمدير
          perms = [
            'access_dashboard', 'view_courses', 'create_course', 'edit_course', 'delete_course',
            'view_course_content', 'create_course_content', 'edit_course_content', 'delete_course_content',
            'view_exams', 'create_exam', 'edit_exam', 'delete_exam', 'view_admins', 'create_admin', 
            'edit_admin', 'delete_admin', 'view_instructors', 'create_instructor', 'edit_instructor',
            'delete_instructor', 'view_trainees', 'create_trainee', 'edit_trainee', 'delete_trainee',
            'view_devices', 'manage_devices', 'delete_devices', 'view_bank_transfers', 'verify_bank_transfers',
            'delete_bank_transfers', 'view_electronic_payments', 'manage_electronic_payments', 'delete_electronic_payments',
            'view_coupons', 'create_coupon', 'edit_coupon', 'delete_coupon', 'view_course_discounts',
            'create_course_discount', 'edit_course_discount', 'delete_course_discount', 'view_books', 'create_book',
            'edit_book', 'delete_book', 'view_reviews', 'create_review', 'edit_review', 'delete_review',
            'view_settings', 'manage_settings', 'view_keywords', 'create_keyword', 'edit_keyword', 'delete_keyword',
            'view_roles', 'edit_roles', 'assign_roles', 'view_instructor_questions', 'answer_instructor_questions',
            'delete_instructor_questions', 'view_categories', 'create_category', 'edit_category', 'delete_category'
          ];
          // تم تطبيق صلاحيات المدير تلقائياً
        }
        
        state.permissions = perms;
        state.roleId = action.payload.roleId || null;
        state.initialized = true;
        state.lastFetch = Date.now();
        
        // حفظ الصلاحيات في sessionStorage للتوافق مع النظام القديم
        sessionStorage.setItem('permissions', JSON.stringify(state.permissions));
        // اضفنا نسخة في userPermissions لحل مشكلة التناقض المحتمل
        sessionStorage.setItem('userPermissions', JSON.stringify(state.permissions));
        
        // لم نعد نستخدم الكوكيز، نعتمد على sessionStorage و Redux فقط
        
        // تم حفظ الصلاحيات في sessionStorage
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'حدث خطأ أثناء جلب الصلاحيات';
        state.initialized = true; // نعتبر المخزن مهيأ حتى لو فشل الطلب
      })
      
      // حالات تحديث صلاحيات الدور
      .addCase(updateRolePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRolePermissions.fulfilled, (state, action) => {
        state.loading = false;
        
        // إذا كان هذا هو دور المستخدم الحالي، نقوم بتحديث الصلاحيات في المتجر
        const userRole = sessionStorage.getItem('userRole');
        if (userRole === action.payload.roleId) {
          state.permissions = action.payload.permissions;
          state.lastFetch = Date.now();
          
          // حفظ الصلاحيات في sessionStorage للتوافق مع النظام القديم
          sessionStorage.setItem('permissions', JSON.stringify(state.permissions));
        }
      })
      .addCase(updateRolePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'حدث خطأ أثناء تحديث الصلاحيات';
      });
  }
});

// تصدير Reducer الصلاحيات
export default permissionsSlice.reducer;

// تصدير Actions
export const { updatePermissions, clearPermissions, setInitialized, setPermissions, setAuthState } = permissionsSlice.actions;

// Selectors للوصول السهل إلى الحالة
export const selectPermissions = (state) => state.permissions.permissions;
export const selectPermissionsLoading = (state) => state.permissions.loading;
export const selectPermissionsError = (state) => state.permissions.error;
export const selectPermissionsInitialized = (state) => state.permissions.initialized;

/**
 * دالة للتحقق من وجود صلاحية معينة
 * @param {Object} state - حالة المتجر
 * @param {string} permission - اسم الصلاحية المطلوب التحقق منها
 * @returns {boolean} - ما إذا كان المستخدم يملك الصلاحية
 */
export const hasPermission = (state, permission) => {
  const { permissions } = state.permissions;
  return Array.isArray(permissions) && permissions.includes(permission);
};
